import { cn } from '../../lib/utils.ts';
import { clockMinute, minuteClock } from '../../lib/staff-display.ts';

/** Eintrag des Tages wie in der Tagesliste (`StaffDayEntry`): Art und Uhrzeiten `HH:MM` aus den Feldern des Servers. */
export interface DayTimelineEntry {
  type: 'clock_in' | 'clock_out' | 'break';
  from: string;
  to: string | null;
  nextDay?: boolean;
}

export interface DayTimelineModel {
  /** Achse in Minuten des Tages (volle Stunden, mindestens 06–20 Uhr). */
  start: number;
  end: number;
  work: { from: number; to: number; open: boolean }[];
  breaks: { from: number; to: number; open: boolean }[];
  points: { key: string; minute: number; kind: 'in' | 'out' }[];
  now: number | null;
}

const AXIS_START = 6 * 60;
const AXIS_END = 20 * 60;

/**
 * Geometrie der Tages-Zeitleiste (rein, ohne Uhr und Netz; nur Darstellung): Kommen und Gehen als Punkte, je Kommen bis
 * Gehen ein Arbeitsband, Pausen von–bis als Abschnitt. Ein offenes Kommen bzw. eine offene Pause reicht bis `now` (nur
 * heute; sonst kein Band). Gehen am Folgetag zählt +24 h. Die Achse umfasst mindestens 06–20 Uhr und wächst auf volle
 * Stunden, wenn ein Stempel oder „jetzt“ außerhalb liegt. Gerechnet wird nichts Fachliches – die Minuten der Arbeitszeit
 * kommen weiter nur vom Server.
 */
export function dayTimelineModel(entries: readonly DayTimelineEntry[], now: number | null): DayTimelineModel {
  const work: DayTimelineModel['work'] = [];
  const breaks: DayTimelineModel['breaks'] = [];
  const points: DayTimelineModel['points'] = [];
  let open: number | null = null;
  entries.forEach((e, i) => {
    const from = clockMinute(e.from);
    if (from === null) return;
    if (e.type === 'clock_in') {
      open = from;
      points.push({ key: `in-${i}`, minute: from, kind: 'in' });
    } else if (e.type === 'clock_out') {
      const out = from + (e.nextDay ? 1440 : 0);
      if (open !== null && out >= open) work.push({ from: open, to: out, open: false });
      open = null;
      points.push({ key: `out-${i}`, minute: out, kind: 'out' });
    } else {
      // Pause nach Mitternacht in einer Nachtschicht (Beginn vor dem Kommen): auf den Folgetag schieben wie das Gehen.
      const start = open !== null && from < open ? from + 1440 : from;
      const to = clockMinute(e.to);
      if (to !== null) {
        const end = to + (start - from);
        breaks.push({ from: start, to: end < start ? end + 1440 : end, open: false });
      } else if (now !== null && now > start) breaks.push({ from: start, to: now, open: true });
    }
  });
  if (open !== null && now !== null && now > open) work.push({ from: open, to: now, open: true });
  const minutes = [...points.map((p) => p.minute), ...breaks.flatMap((b) => [b.from, b.to]), ...(now !== null ? [now] : [])];
  const lo = Math.min(AXIS_START, ...minutes.map((m) => Math.floor(m / 60) * 60));
  const hi = Math.max(AXIS_END, ...minutes.map((m) => Math.ceil(m / 60) * 60));
  return { start: lo, end: hi, work, breaks, points, now };
}

/** Lage auf der Achse in Prozent (0–100, auf 0,1 gerundet). */
export function axisPercent(minute: number, model: Pick<DayTimelineModel, 'start' | 'end'>): number {
  const span = model.end - model.start;
  if (span <= 0) return 0;
  const p = ((minute - model.start) * 100) / span;
  return Math.round(Math.min(100, Math.max(0, p)) * 10) / 10;
}

/** Stunden-Marken alle zwei Stunden („06“, „08“ …). */
function ticks(model: DayTimelineModel): number[] {
  const out: number[] = [];
  for (let m = model.start; m <= model.end; m += 120) out.push(m);
  return out;
}

/** Text an einer Position ohne Überlauf am Rand: links bündig am Anfang, rechts bündig am Ende, sonst mittig. */
function anchorClass(percent: number): string {
  if (percent < 8) return 'translate-x-0';
  if (percent > 92) return '-translate-x-full';
  return '-translate-x-1/2';
}

/**
 * Tages-Zeitleiste (Vorbild Apple Bildschirmzeit/Kalender-Tag, Ticket 1.284 Nacharbeit): waagerechte Achse 06–20 Uhr,
 * Arbeit als gefülltes Band (`capsule`) auf weißer Spur (für die Fläche `tile` gedacht), Pause als gestrichelter Abschnitt
 * im Band, Kommen und Gehen als Punkte (Legende darunter), heute die Marke „jetzt“ (Zeit der fachlichen Uhr, `now`). Für
 * Hilfsmittel ein Bild mit Satz (`label`); die bedienbaren Zeilen stehen darunter in der Tagesliste. Keine Dauer-Animation;
 * das Band blendet nur einmal ein.
 */
export function DayTimeline({ entries, now, label, testId, className }: { entries: readonly DayTimelineEntry[]; now: number | null; label: string; testId?: string; className?: string }) {
  const model = dayTimelineModel(entries, now);
  const at = (m: number) => `${axisPercent(m, model)}%`;
  const width = (a: number, b: number) => `${Math.max(0, axisPercent(b, model) - axisPercent(a, model))}%`;
  const nowPct = model.now !== null ? axisPercent(model.now, model) : null;
  return (
    <div role="img" aria-label={label} data-day-timeline="" data-testid={testId} className={cn('flex min-w-0 flex-col gap-space-1', className)}>
      <div aria-hidden className="relative h-space-5">
        {nowPct !== null ? (
          <span data-day-timeline-now-label="" className={cn('absolute bottom-0 type-meta font-medium whitespace-nowrap text-link tabular-nums', anchorClass(nowPct))} style={{ left: `${nowPct}%` }}>
            jetzt {minuteClock(model.now!)}
          </span>
        ) : null}
      </div>
      <div aria-hidden className="relative h-space-8">
        <span className="absolute inset-x-0 top-1/2 h-space-2 -translate-y-1/2 rounded-full bg-card" />
        {ticks(model).map((m) => (
          <span key={m} className="absolute top-1/2 h-space-3 w-px -translate-y-1/2 bg-line" style={{ left: at(m) }} />
        ))}
        {model.work.map((w, i) => (
          <span
            key={`w-${i}`}
            data-day-timeline-work=""
            data-open={w.open ? '' : undefined}
            className={cn('absolute top-1/2 h-space-4 -translate-y-1/2 animate-fade-in rounded-full bg-capsule', w.open && 'rounded-r-none')}
            style={{ left: at(w.from), width: width(w.from, w.to) }}
          />
        ))}
        {model.breaks.map((b, i) => (
          <span
            key={`b-${i}`}
            data-day-timeline-break=""
            className="absolute top-1/2 h-space-4 -translate-y-1/2 rounded-full border-2 border-dashed border-capsule bg-card"
            style={{ left: at(b.from), width: width(b.from, b.to) }}
          />
        ))}
        {model.points.map((p) => (
          <span
            key={p.key}
            data-day-timeline-point={p.kind}
            className={cn('absolute top-1/2 size-space-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-card', p.kind === 'in' ? 'bg-primary' : 'bg-ready')}
            style={{ left: at(p.minute) }}
          />
        ))}
        {nowPct !== null ? <span data-day-timeline-now="" className="absolute inset-y-0 w-0.5 -translate-x-1/2 rounded-full bg-link" style={{ left: `${nowPct}%` }} /> : null}
      </div>
      <div aria-hidden className="relative h-space-4">
        {ticks(model).map((m) => {
          const p = axisPercent(m, model);
          return (
            <span key={m} className={cn('absolute top-0 text-caption text-ink-3 tabular-nums', anchorClass(p))} style={{ left: `${p}%` }}>
              {minuteClock(m).slice(0, 2)}
            </span>
          );
        })}
      </div>
      <div aria-hidden className="flex flex-wrap gap-x-space-4 gap-y-space-1 pt-space-1 type-meta text-ink-3">
        <span className="inline-flex items-center gap-space-2">
          <span className="size-space-3 rounded-full bg-primary" />
          Kommen
        </span>
        <span className="inline-flex items-center gap-space-2">
          <span className="h-space-2 w-space-5 rounded-full bg-capsule" />
          Arbeit
        </span>
        <span className="inline-flex items-center gap-space-2">
          <span className="h-space-2 w-space-5 rounded-full border-2 border-dashed border-capsule" />
          Pause
        </span>
        <span className="inline-flex items-center gap-space-2">
          <span className="size-space-3 rounded-full bg-ready" />
          Gehen
        </span>
      </div>
    </div>
  );
}
